import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, Wheat } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistDialog = ({ open, onOpenChange, onJoined }) => {
  const [form, setForm] = useState({ name: "", email: "", farm_name: "", county: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setForm({ name: "", email: "", farm_name: "", county: "" });
    setError("");
    setDone(null);
    setLoading(false);
  };

  const handleClose = (v) => {
    if (!v) setTimeout(reset, 250);
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, form);
      setDone(res.data);
      onJoined && onJoined(res.data.total);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please check your email and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const firstName = form.name.trim().split(" ")[0] || "there";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="sm:max-w-[460px] border-[#E8E5DF] bg-[#FCFCFA] rounded-[20px] p-0 overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 text-center"
              data-testid="waitlist-success"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#E6EBE1]">
                <Check className="h-7 w-7 text-[#2FA84F]" strokeWidth={3} />
              </div>
              <h3 className="text-2xl font-bold text-[#14401F]">
                Thanks, {firstName}.
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[#1A1C18]/70">
                We'll be in touch the moment AgriStock opens for your farm.
              </p>
              {done.position ? (
                <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F5F2ED] px-4 py-2 text-sm font-semibold text-[#14401F]">
                  <Wheat className="h-4 w-4" />
                  You're #{done.position} on the list
                </p>
              ) : null}
              <Button
                data-testid="waitlist-close-btn"
                onClick={() => handleClose(false)}
                className="mt-7 w-full rounded-full bg-[#14401F] py-6 text-base font-semibold text-white hover:bg-[#0d2d15]"
              >
                Done
              </Button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={submit}
              className="p-8"
            >
              <DialogHeader className="text-left">
                <DialogTitle className="text-2xl font-bold text-[#14401F]">
                  Join the waitlist
                </DialogTitle>
                <DialogDescription className="text-[#1A1C18]/60">
                  Be first through the gate when AgriStock opens for UK farms.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="wl-name" className="text-sm font-medium text-[#1A1C18]">
                    Your name <span className="text-[#C85A3C]">*</span>
                  </Label>
                  <Input
                    id="wl-name"
                    data-testid="waitlist-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="John Smith"
                    className="rounded-xl border-[#E8E5DF] bg-white py-5 focus-visible:ring-[#14401F]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wl-email" className="text-sm font-medium text-[#1A1C18]">
                    Email <span className="text-[#C85A3C]">*</span>
                  </Label>
                  <Input
                    id="wl-email"
                    data-testid="waitlist-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@farm.co.uk"
                    className="rounded-xl border-[#E8E5DF] bg-white py-5 focus-visible:ring-[#14401F]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="wl-farm" className="text-sm font-medium text-[#1A1C18]">
                      Farm name
                    </Label>
                    <Input
                      id="wl-farm"
                      data-testid="waitlist-farm-input"
                      value={form.farm_name}
                      onChange={update("farm_name")}
                      placeholder="Green Valley Farm"
                      className="rounded-xl border-[#E8E5DF] bg-white py-5 focus-visible:ring-[#14401F]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wl-county" className="text-sm font-medium text-[#1A1C18]">
                      County
                    </Label>
                    <Input
                      id="wl-county"
                      data-testid="waitlist-county-input"
                      value={form.county}
                      onChange={update("county")}
                      placeholder="Norfolk"
                      className="rounded-xl border-[#E8E5DF] bg-white py-5 focus-visible:ring-[#14401F]"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <p data-testid="waitlist-error" className="mt-4 text-sm text-[#C85A3C]">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                data-testid="waitlist-submit-btn"
                disabled={loading}
                className="mt-6 w-full rounded-full bg-[#14401F] py-6 text-base font-semibold text-white hover:bg-[#0d2d15]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Joining…
                  </>
                ) : (
                  "Join the waitlist"
                )}
              </Button>
              <p className="mt-3 text-center text-xs text-[#1A1C18]/45">
                14-day free trial when we launch · no card needed
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
